import { useState } from 'react';
import { fmtDateTime, fmtDay, fmtMonDayOrdinal, GRAND_ALL, GRAND_PRIZE_COUNT } from '../campaign';
import { Crop, BgRot, PrizeArt, StatBox } from './Shared';
import { gemsChipFor, chestsChipFor } from './Hero';
import { BackIcon, LockBtnIcon, RewardIcon } from './Icons';
import GemBreak from './GemBreak';
import { SurveyRow } from './Survey';

/* ------------------------------------------------------------------
   "Your rewards" — full-screen page over the hub with its own scroller.
   Gems tab: reserved / ready-to-open cells and the opened list; tapping
   Open brings up the Gem-break stage (GemBreak.js), which calls
   onOpen(gemId) (the API) and reveals the reward; the cell then
   reappears under "Opened". Chests tab: every Gold Chest and its draw
   status. `gems` / `chests` are the mapped wallet lists; the parent
   updates them when onOpen resolves. While the quick survey is open to
   the user (`survey` + `surveyState`, not done), both tabs lead with its
   entry row; `onSurvey` opens it.
   ------------------------------------------------------------------ */

const STATUS_BY_KIND = { GOLD: 'Credited', SATS: 'Credited', CASHBACK: 'Credited', VOUCHER: 'Saved', BOOST: 'Active', PENDING: 'Pending' };

export default function Rewards({
  phase, tab, onTab, gems, chests, grandTotal = GRAND_PRIZE_COUNT, drawDay = '13 November', launch, drawOn,
  drawn = phase === 'ended', survey = null, surveyState = null, onSurvey,
  zoom = 1, stageHeight = 812, onBack, onOpen, onError,
}) {
  const [breaking, setBreaking] = useState(null); // the Gem on the break stage

  const pre = phase === 'pre';
  const ended = phase === 'ended';
  /* `drawn`: the reveal day (grandPrizes.drawOn) has come, or the draw is
     published — the chests read Drawn / Won. Until then, even once the
     campaign has ended, they are still "In the draw". */
  const surveyRow = survey && surveyState && survey.takeable && surveyState.status !== 'done' && !ended
    ? <SurveyRow survey={survey} state={surveyState} onClick={onSurvey} />
    : null;
  const ready = gems.filter((g) => !g.opened);
  const opened = gems.filter((g) => g.opened).sort((a, b) => b.opened.at - a.opened.at);
  const unopened = ready.length;
  const others = breaking ? ready.filter((g) => g.uid !== breaking) : [];
  const gemsChip = gemsChipFor(phase, gems.length, unopened, { launchLabel: launch ? fmtMonDayOrdinal(launch) : 'Oct 18th' });
  const chestsChip = chestsChipFor(phase, chests.length, { drawLabel: drawOn ? fmtMonDayOrdinal(drawOn) : 'Nov 13th', drawn });

  return (
    <div className="gq-overlay gq-scrollable">
      <div className="gq-rewards" style={{ zoom, height: stageHeight }}>
        <BgRot />

        <div className="gq-scroll" aria-hidden={!!breaking}>
          <div className="gq-hdr">
            <button type="button" className="gq-back" onClick={onBack} aria-label="Back"><BackIcon /></button>
            <div className="gq-ttl">Your rewards</div>
          </div>
          <div className="gq-sum">
            <StatBox kind="gems" num={gems.length} label="Gems" chip={gemsChip} />
            <StatBox kind="chests" num={chests.length} label="Chests" chip={chestsChip} />
          </div>
          <div className="gq-seg" role="tablist">
            {['gems', 'chests'].map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'gq-on' : ''} onClick={() => onTab(t)}>
                {t === 'gems' ? 'Gems' : 'Chests'}
              </button>
            ))}
          </div>

          {tab === 'gems' ? (
            <>
              {surveyRow}
              {!ready.length && !opened.length && (
                <div className="gq-rempty">No Gems yet. Spend ₹500 on your card, buy a voucher or gold, or refer a friend to earn your first.</div>
              )}
              {ready.length > 0 && (
                <>
                  <div className="gq-rsec">{pre ? 'Reserved' : 'Ready to open'}<span>{ready.length}</span></div>
                  <div className="gq-ggrid">
                    {
                    ready.map((g, i) => (
                      <div key={g.uid} className={`gq-gcell${pre ? ' gq-locked' : ''}`} style={{ '--i': i }}>
                        <Crop kind="gem" />
                        <div className="gq-src">{g.sourceLabel}</div>
                        <div className="gq-dt">{pre ? 'Unlocks 18 Oct' : fmtDateTime(g.earnedAt)}</div>
                        <button type="button" className="gq-open" disabled={pre} onClick={() => setBreaking(g.uid)}>
                          {pre ? <><LockBtnIcon width="12" height="14" />Locked</> : 'Open'}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {opened.length > 0 && (
                <>
                  <div className="gq-rsec">Opened<span>{opened.length}</span></div>
                  <div className="gq-rlist">
                    {opened.map((g, i) => (
                      <div className="gq-rrow" key={g.uid} style={{ '--i': i }}>
                        <div className="gq-ic"><RewardIcon kind={g.opened.reward.kind} /></div>
                        <div>
                          <div className="gq-rn">{g.opened.reward.name}</div>
                          <div className="gq-rs">{g.sourceLabel} · {fmtDateTime(g.opened.at)}</div>
                        </div>
                        <div className="gq-st">{STATUS_BY_KIND[g.opened.reward.kind] || 'Credited'}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {surveyRow}
              {!chests.length ? (
                <div className="gq-rempty">
                  No chests yet. Complete all four steps of any streak to earn a Gold Chest. Every chest wins, and {grandTotal} grand prizes are drawn on {drawDay}.
                </div>
              ) : (
                <>
                  <div className="gq-rsec">Your chests<span>{chests.length} in the draw</span></div>
                  <div className="gq-rlist">
                    {chests.map((c, i) => {
                      const grand = drawn && c.result && c.result.kind === 'GRAND';
                      /* a grand prize we have a render for: named in the sub-line, pictured in place of the status pill */
                      const pictured = grand && c.result.prize && GRAND_ALL.some((g) => g.name === c.result.prize);
                      const fromSurvey = c.streak === 'survey'; // the survey's chest isn't a streak: "Survey bonus chest"
                      /* every chest opens on the reveal day (grandPrizes.drawOn); from then on it reads what came of it */
                      let status = `Opens ${drawOn ? fmtDay(drawOn) : drawDay}`;
                      if (drawn) status = c.result ? `Won · ${c.result.prize || c.result.kind}` : 'Drawn';
                      return (
                        <div className="gq-rrow" key={c.id} style={{ '--i': i }}>
                          <div className="gq-ic gq-ch"><Crop kind="chest" /></div>
                          <div>
                            <div className="gq-rn">{fromSurvey ? `${c.streakName} chest` : `${c.streakName} streak chest`}</div>
                            <div className="gq-rs">
                              {pictured
                                ? <>Won <b>{c.result.prize}</b> · {drawOn ? fmtDay(drawOn) : drawDay}</>
                                : <>Earned {fmtDateTime(c.earnedAt)}</>}
                            </div>
                          </div>
                          {pictured
                            ? <div className="gq-pz"><PrizeArt prize={{ name: c.result.prize }} /></div>
                            : <div className={`gq-st${grand ? ' gq-gold' : ''}`}>{status}</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="gq-rline">
                    {drawn
                      ? `Winners were drawn on ${drawDay}. Delivery details go out by SMS.`
                      : `Every chest wins. ${grandTotal} grand prizes are drawn at random on ${drawDay}; every other chest wins Sats or Gold.`}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {breaking && (
          <GemBreak
            gemId={breaking}
            others={others.length}
            nextId={others.length ? others[0].uid : null}
            onOpen={onOpen}
            onError={onError}
            onNext={setBreaking}
            onClose={() => setBreaking(null)}
            stageHeight={stageHeight}
          />
        )}
      </div>
    </div>
  );
}
